import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/Button';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { VideoPanel } from '../components/VideoPanel';
import { useExam } from '../context/ExamContext';
import { useNavigate } from 'react-router-dom';
import { WarningSoundControl } from '../components/WarningSoundControl';

export function PermissionCheckPage() {
  const check = usePermissionCheck();
  const { completeWorkflowStep } = useExam();
  const navigate = useNavigate();
  const { items, checking, allPassed } = check;
  return (
    <section className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-3xl font-bold">Permission Check</h1>
      <p className="mt-2 text-slate-600 dark:text-slate-300">Start the checks to allow webcam and microphone access and verify browser readiness.</p>
      {items.length === 0 ? <Button className="mt-6" onClick={() => void check.retry()}>Start Permission Checks</Button> : null}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {items.map((item) => (
          <div key={item.key} className="rounded-md border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              {item.state === 'checking' ? <Loader2 className="h-6 w-6 animate-spin" /> : item.ok ? <CheckCircle2 className="h-6 w-6 text-emerald-500" /> : <XCircle className="h-6 w-6 text-red-500" />}
              <h2 className="font-semibold">{item.label}</h2>
            </div>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">{item.detail}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <VideoPanel stream={check.stream} label="Permission preview" />
        <div className="glass space-y-4 rounded-md p-5">
          <label className="block text-sm font-semibold">Camera<select className="mt-2 w-full rounded-md border p-2" value={check.cameraId} onChange={(event) => check.setCameraId(event.target.value)}><option value="">System default</option>{check.devices.filter((d) => d.kind === 'videoinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Camera'}</option>)}</select></label>
          <label className="block text-sm font-semibold">Microphone<select className="mt-2 w-full rounded-md border p-2" value={check.microphoneId} onChange={(event) => check.setMicrophoneId(event.target.value)}><option value="">System default</option>{check.devices.filter((d) => d.kind === 'audioinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>)}</select></label>
          <div><span className="text-sm font-semibold">Microphone activity</span><div className="mt-2 h-2 rounded bg-slate-700"><div className="h-full rounded bg-emerald-400" style={{ width: `${check.audioLevel}%` }} /></div></div>
          <Button variant="secondary" onClick={() => void check.retry()}>{items.length ? 'Retry checks' : 'Start checks'}</Button>
          <WarningSoundControl />
        </div>
      </div>
      <p className="mt-5 font-semibold">Readiness: {items.length === 0 ? 'Not checked' : allPassed ? 'Ready' : items.some((item) => item.state === 'denied') ? 'Not ready' : 'Ready with warnings'}</p>
      <div className="mt-8 flex gap-3">
        <Button disabled={checking || !allPassed} onClick={() => { completeWorkflowStep('permissionsCompleted'); navigate('/camera-setup'); }}>{checking ? 'Checking...' : 'Continue to Camera Setup'}</Button>
        <Button variant="secondary" onClick={() => { completeWorkflowStep('permissionsCompleted'); navigate('/camera-setup'); }}>Continue in Demo Mode</Button>
      </div>
    </section>
  );
}
